import { Alert, Badge, Button, Card, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from 'mantine-datatable';
import { Fragment, type ReactNode, useMemo, useState } from 'react';
import { rawApi } from '../api/client';
import { errorCopy } from '../api/errors';
import { listKey } from '../api/queryKeys';
import { OverShort } from '../components/CashFigures';
import { EntityLabel } from '../components/EntityField';
import { nameFor } from '../components/RangeWarnings';
import { useBusinessDate } from '../hooks/useSeedLocation';
import { enumColor, enumLabel } from '../lib/enums';
import { formatDateTime, formatMoney, toIsoDate } from '../lib/format';
import { Exceptions } from './reports/Exceptions';
import { MenuProfitability } from './reports/MenuProfitability';
import { QualityRecovery } from './reports/QualityRecovery';
import { ReferralLeaderboard } from './reports/ReferralLeaderboard';
import { StockVariance } from './reports/StockVariance';

/**
 * THE BAR'S MONEY: what was sold, how it was paid for, and what was left after the food.
 *
 * Reordered on 2026-08-10 around the question the owner actually asked — *"Give me total sales...
 * breakdowns by how people are paying, cash, credit, transfers... Are we making money or not?"* —
 * because the page opened on a sales panel and then spent eight sections on tax, tips and invoice
 * numbering before it said anything about whether the business was ahead. Nothing has been taken
 * away: the tax return, the tips and the numbering are all still here, behind the answer.
 *
 * **There is no bottom line on this page and there must not be one.** Rent, power, water, the card
 * processor's cut and the delivery platforms' commission are recorded nowhere in this system, so
 * what can honestly be struck is GROSS MARGIN — what came in after tax, less the food and drink
 * that actually left the shelf — and the missing categories are printed under it by name. A profit
 * figure built from half the costs would be believed, quoted, and wrong.
 *
 * Every figure here is read-only and derived from the same records the SAR return is built from,
 * so nothing on this page can be edited — it can only be checked.
 */

/**
 * Documents in the range that were issued without a tax split, per currency.
 *
 * The sales figures count them and the tax figures cannot, so the two halves of this page are
 * measuring different sets of documents whenever this is not empty. Saying so is the whole job of
 * this type: nothing here fills the gap in, because a tax figure invented for a document that was
 * issued without one would be worse than the gap.
 */
interface WithoutTaxDetail {
  currency: string;
  documentCount: number;
  totalMinor: number;
}

interface SalesSummary {
  documentCount: number;
  byCurrency: {
    currency: string;
    documentCount: number;
    grossMinor: number;
    taxMinor: number;
    netMinor: number;
  }[];
  byType: { type: string; currency: string; count: number; totalMinor: number }[];
  byChannel: { salesChannelId: string | null; currency: string; count: number; totalMinor: number }[];
  withoutTaxDetail: WithoutTaxDetail[];
}

interface TaxReport {
  byComponent: {
    code: string;
    currency: string;
    netMinor: number;
    taxMinor: number;
    grossMinor: number;
  }[];
  /** One row per tax, with the base it was charged on. See the section for why they are separate. */
  byTax: {
    code: string;
    currency: string;
    netMinor: number;
    taxMinor: number;
  }[];
  withoutTaxDetail: WithoutTaxDetail[];
}

interface DrawerSession {
  drawerSessionId: string;
  ownerStaffId: string;
  openedAt: string;
  closedAt: string | null;
  status: string;
  overShortUsdMinor: number | null;
  overShortHnlMinor: number | null;
}

interface CashReconciliation {
  sessions: DrawerSession[];
}

/** A tax set's components, for turning the stored code on a document back into its name. */
interface TaxGroupFull {
  components: { component: { code: string; name: string } }[];
}

interface FiscalRegister {
  documents: number;
  byEmissionPoint: {
    emissionPointId: string;
    count: number;
    firstNumber: number | null;
    lastNumber: number | null;
    gaps: number[];
  }[];
  /** The documents the count includes and the table cannot list, by kind. */
  notNumbered: { type: string; count: number }[];
}

interface TipBucket {
  staffId: string;
  currency: string;
  totalMinor: number;
}

interface TipsReport {
  nonCash: TipBucket[];
  cashDeclared: TipBucket[];
  unpaidLiability: TipBucket[];
}

/** Money, per currency, never summed. A figure without one is a figure nobody can bank. */
interface CurrencyAmount {
  currency: string;
  minor: number;
}

/**
 * The money answer: sales, the payment mix, gross margin, and the costs this system happens to hold.
 *
 * One call rather than three panels stitched together, because the honesty of it is in the
 * relationships — what was invoiced against what was really settled, what was presented against
 * what was kept, what came in against what left the shelf — and a page that fetched those from
 * three places would let them disagree without saying so.
 */
interface MoneyReport {
  baseCurrency: string;
  sales: {
    currency: string;
    documentCount: number;
    totalInvoicedMinor: number;
    salesBeforeTaxMinor: number;
    taxChargedMinor: number;
  }[];
  platformSales?: {
    source: string;
    channelId: string;
    currency: string;
    orderCount: number;
    grossMinor: number;
    taxMinor: number;
    netMinor: number;
  }[];
  salesWithoutTaxDetail: WithoutTaxDetail[];
  settledWithoutInvoice: { currency: string; checkCount: number; totalMinor: number }[];
  givenAway: { currency: string; discountMinor: number; compMinor: number }[];
  payment: {
    tenders: {
      type: string;
      currency: string;
      count: number;
      /** What was PRESENTED — the face value that crossed the counter, change still inside it. */
      handedOverMinor: number;
      tipsMinor: number;
    }[];
    changeGivenByCurrency: CurrencyAmount[];
    /** The part of that handed back in a money nobody paid in. A subset, never netted off. */
    changeAcrossCurrencies: CurrencyAmount[];
    keptByCurrency: CurrencyAmount[];
    tipsByCurrency: CurrencyAmount[];
    roundingByCurrency: CurrencyAmount[];
    onAccount: { currency: string; count: number; totalMinor: number }[];
    accountTransfers: { currency: string; count: number; totalMinor: number }[];
  };
  goodsSold: {
    revenueBaseMinor: number;
    costBaseMinor: number;
    grossMarginBaseMinor: number;
    marginBp: number | null;
    dishesCosted: number;
    dishesWithNoCost: number;
    recipeFaults: number;
    unpricedIngredients: number;
    billsCounted: number;
    billsWithNoRecordedRate: number;
    billsThatWouldNotAddUp: number;
  };
  otherCosts: {
    supplierBills: { currency: string; count: number; totalMinor: number }[];
    payroll: {
      payrollRunId: string;
      periodStart: string;
      periodEnd: string;
      status: string;
      withinRange: boolean;
      totalsByCurrency: CurrencyAmount[];
    }[];
    referralCommission: CurrencyAmount[];
    /** Never rendered without `drawersOff` beside it — see the panel for what a net zero hides. */
    drawerOverShort: CurrencyAmount[];
    drawersCounted: number;
    drawersOff: number;
    drawersNotCounted: number;
  };
  /** Categories of cost recorded nowhere. Named by the server so the list cannot go stale here. */
  costsNotRecorded: string[];
}

interface EmissionPoint {
  id: string;
  businessName: string;
  establishmentCode: string;
  emissionPointCode: string;
  /**
   * What this block of numbers prints, and which register prints from it. Both are needed to NAME it:
   * an invoice range and a credit note range may carry the same establishment and the same point
   * code, and without these two the screen prints one string for both.
   */
  documentType: 'invoice' | 'creditNote' | 'debitNote';
  deviceId: string | null;
}

interface ReportBundle {
  sales: SalesSummary;
  money: MoneyReport;
  tax: TaxReport;
  fiscal: FiscalRegister;
  tips: TipsReport;
  cash: CashReconciliation;
}

/** One row of the tips table: both kinds of tip for one person, in one currency. */
interface TipLine {
  key: string;
  staffId: string;
  currency: string;
  nonCashMinor: number;
  cashMinor: number;
  totalMinor: number;
}

/**
 * Invoice numbers as they are printed on the paper in the owner's hand: the last block of the
 * 16-digit number, zero-padded. Checking "43 is missing" against a stack of invoices reading
 * 00000043 is a job of comparing two things that do not look alike.
 */
const invoiceNumber = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? String(n).padStart(8, '0') : '—';

const plural = (n: number, noun: string) =>
  `${n} ${n === 1 ? noun : noun === 'dish' ? 'dishes' : `${noun}s`}`;

/**
 * "2 documents taking $3.00 back off the sales" — the sign spoken as a word, never as a minus.
 *
 * These are nearly always credit notes, so the total is nearly always negative, and a bare
 * "totaling $ -3.00" in the middle of a sentence is the kind of figure a reader silently reads as
 * positive. `OverShort` on the cash figures above already settled this: print the amount unsigned
 * and say which way it goes.
 */
function withoutTaxPhrase(row: WithoutTaxDetail): string {
  const documents = plural(row.documentCount, 'document');
  if (row.totalMinor === 0) return documents;
  const amount = formatMoney(Math.abs(row.totalMinor), row.currency);
  return row.totalMinor < 0
    ? `${documents} taking ${amount} back off the sales`
    : `${documents} adding ${amount} to the sales`;
}

const withoutTaxSentence = (rows: WithoutTaxDetail[]) => rows.map(withoutTaxPhrase).join(', and ');

/**
 * The verb that agrees with the subject `withoutTaxSentence` just built.
 *
 * Two sentences on this page describe the same set of documents, and each picked its own verb: the
 * sales note counted the documents and said "was issued"/"were issued", the tax note hard-coded
 * "carry" — so the one figure on this page that gets copied onto a government form sat under
 * *"1 document taking $ 3.00 back off the sales in these dates carry no tax split"*. One source,
 * written down in two places, is this repository's own favorite defect.
 */
const withoutTaxVerb = (rows: WithoutTaxDetail[], one: string, many: string) =>
  rows.reduce((n, r) => n + r.documentCount, 0) === 1 ? one : many;

const localDate = (d: Date) => toIsoDate(d) ?? '';

/**
 * EVERY PRESET IS ANCHORED ON THE BAR'S TRADING DAY, never on the browser's calendar.
 *
 * Two fixes, and only the first one had been made. The previous version used `toISOString()`, which
 * is UTC: after 6pm in Roatán "today" became tomorrow and a month range could start after it ended.
 * That was replaced with local calendar parts — which is the READER's calendar, and still not the
 * bar's. Both remaining halves matter here:
 *
 *   - the reader may be anywhere. These screens get opened from another country often enough that
 *     the browser's date is simply a different date from the one the bar is trading on;
 *   - the trading day rolls at 04:00, and every report below ranges on `business_days.business_date`.
 *     Between midnight and four, with service still going, a browser-derived "Today" names a night
 *     the bar has not started — so the page reads zero while the bar is full.
 *
 * So the anchor is a `YYYY-MM-DD` handed in by `useBusinessDate()`, and the arithmetic below works
 * off it. Noon, because a date parsed at midnight and then shifted by days is one daylight-saving
 * transition away from landing on the day before.
 */
const at = (day: string) => new Date(`${day}T12:00:00`);

const today = (day: string): [string, string] => [day, day];

const thisWeek = (day: string): [string, string] => {
  const end = at(day);
  const start = at(day);
  start.setDate(start.getDate() - ((end.getDay() + 6) % 7)); // weeks start Monday
  return [localDate(start), day];
};

const thisMonth = (day: string): [string, string] => {
  const end = at(day);
  return [localDate(new Date(end.getFullYear(), end.getMonth(), 1)), day];
};

const lastMonth = (day: string): [string, string] => {
  const end = at(day);
  return [
    localDate(new Date(end.getFullYear(), end.getMonth() - 1, 1)),
    localDate(new Date(end.getFullYear(), end.getMonth(), 0)),
  ];
};

const PRESETS = [
  { label: 'Today', range: today },
  { label: 'This week', range: thisWeek },
  { label: 'This month', range: thisMonth },
  { label: 'Last month', range: lastMonth },
];

function ReportSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Title order={5}>{title}</Title>
      {description && (
        <Text size="sm" c="dimmed" mt={2} mb="sm">
          {description}
        </Text>
      )}
      {children}
    </Paper>
  );
}

/**
 * `value` is a `ReactNode`, so the box it sits in cannot be a paragraph.
 *
 * Mantine's `Text` renders a `<p>`, and the figures on this page are not all strings — the cash
 * numbers pass `<OverShort>`, which is another `Text`, and the register page's figures pass
 * `<MoneyPair>`, which is a `Group` and therefore a `<div>`. Either one inside a `<p>` is invalid
 * HTML: the browser closes the paragraph at the nested block, React logs *"<p> cannot be a
 * descendant of <p>. This will cause a hydration error"*, and what the server rendered and what the
 * client rebuilds no longer match.
 *
 * `component="div"` keeps the weight and the size — which the string figures still need — without
 * making a promise about what may go inside. The equivalent boxes on the register pages already do
 * it this way, by rendering `value` bare.
 */
function Figure({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <Card withBorder>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text component="div" fw={700} size="lg">
        {value}
      </Text>
      {hint && (
        <Text component="div" size="xs" c="dimmed">
          {hint}
        </Text>
      )}
    </Card>
  );
}

/**
 * AN AMOUNT THAT CAN GO EITHER WAY, PRINTED UNSIGNED WITH THE DIRECTION AS A WORD.
 *
 * This page already wrote the rule down for its credit-note sentence — *"print the amount unsigned
 * and say which way it goes; `OverShort` on the cash figures above already settled this"* — and
 * then handed three new figures that can go negative straight to `formatMoney`, which renders
 * `"$ -223.55"`. A bare minus in a large bold number is read as positive by anybody scanning, and
 * two of the three are the figures he would quote.
 *
 * `OverShort` itself is not reusable here: "short" and "over" are drawer words and mean something
 * specific about a count. These take their own two words from the caller.
 */
function Directed({
  minor,
  currency,
  up,
  down,
}: {
  minor: number;
  currency: string;
  /**
   * The word for a positive figure — and for a zero, which is not a bad direction. Empty where the
   * label above already says which way a positive goes and a word would only repeat it.
   */
  up: string;
  down: string;
}) {
  const word = minor < 0 ? down : up;
  return (
    <Text component="span" c={minor < 0 ? 'red' : undefined} fw={700} size="lg">
      {formatMoney(Math.abs(minor), currency)}
      {word ? ` ${word}` : ''}
    </Text>
  );
}

/**
 * An empty state is a CLAIM about the dates, and while the figures are in flight there is no claim
 * to make.
 *
 * Every one of these panels reads `money`, which is undefined on the first render and again on
 * every preset click — the query is keyed on the dates and carries no placeholder, deliberately, so
 * that one range's numbers can never sit under another range's heading. What it must not do in the
 * meantime is assert: for a second on every click the page said *"No supplier bills are dated in
 * these days"*, *"No pay period covers these dates"*, and *"Left over $0.00"*, in the same words it
 * uses when that is true. The panel above them had already learned this and carries `&& !loading`.
 */
function Nothing({ loading, children }: { loading: boolean; children: ReactNode }) {
  return (
    <Text size="sm" c="dimmed">
      {loading ? 'Working this out…' : children}
    </Text>
  );
}

export function ReportsPage() {
  // The night the bar is on right now. Undefined until the location has loaded, and that is not
  // the same as today: nothing is fetched until it is known, rather than fetching the reader's
  // idea of the date and correcting it afterwards.
  const businessToday = useBusinessDate();
  const [chosen, setChosen] = useState<[string, string] | null>(null);
  const [from, to] = chosen ?? (businessToday ? thisMonth(businessToday) : ['', '']);

  // One query for all five reports, keyed on the dates. A part-loaded set used to be possible:
  // five separate state slots meant a single failure left the previous range's numbers on screen
  // under the new dates, which is worse than showing nothing.
  const reports = useQuery({
    queryKey: ['reports', from, to],
    queryFn: async (): Promise<ReportBundle> => {
      const range = `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
      // `tender-mix` is deliberately NOT here any more: the money report answers the same
      // question with the same implementation behind it, plus the three figures a tender row
      // cannot hold. Two panels reading two endpoints for one fact is how they come to disagree.
      const [sales, money, tax, fiscal, tips, cash] = await Promise.all([
        rawApi<SalesSummary>('GET', `/admin/v1/reports/sales-summary?${range}`),
        rawApi<MoneyReport>('GET', `/admin/v1/reports/money?${range}`),
        rawApi<TaxReport>('GET', `/admin/v1/reports/tax?${range}`),
        rawApi<FiscalRegister>('GET', `/admin/v1/reports/fiscal-register?${range}`),
        rawApi<TipsReport>('GET', `/admin/v1/reports/tips?${range}`),
        rawApi<CashReconciliation>('GET', `/admin/v1/reports/cash-reconciliation?${range}`),
      ]);
      return { sales, money, tax, fiscal, tips, cash };
    },
    enabled: Boolean(from && to),
  });

  // The document stores the tax's code; the tax set stores what it is called. Reading "ISV15" off
  // the return you are about to file is reading our database, not your paperwork.
  const taxGroups = useQuery({
    queryKey: listKey('/admin/v1/tax/groups'),
    queryFn: () => rawApi<TaxGroupFull[]>('GET', '/admin/v1/tax/groups'),
    staleTime: 60_000,
  });

  const taxName = useMemo(() => {
    const byCode = new Map<string, string>();
    for (const group of taxGroups.data ?? []) {
      for (const { component } of group.components ?? []) byCode.set(component.code, component.name);
    }
    // A sale can fall under two taxes at once, in which case the pair is reported together.
    return (code: string) =>
      code === 'EXEMPT'
        ? 'Exempt sales'
        : code
            .split('+')
            .map((part) => byCode.get(part) ?? part)
            .join(' + ');
  }, [taxGroups.data]);

  // Invoice books are not in the shared reference registry, so this page resolves them itself.
  const emissionPoints = useQuery({
    queryKey: listKey('/admin/v1/fiscal/emission-points'),
    queryFn: () => rawApi<EmissionPoint[]>('GET', '/admin/v1/fiscal/emission-points'),
    staleTime: 60_000,
  });

  /*
   * WHICH BOOK OF NUMBERS THIS ROW IS, said in a way that tells two books apart.
   *
   * This used to compose `000-001 · Deco Stop Bar & Grill` out of the establishment code, the point
   * code and the business name — none of which distinguishes an invoice range from a credit note
   * range. Both are `000-001` at this bar, so on 2026-07-28 the table printed two rows that were
   * character-for-character identical, under a heading that tells the owner to check it before he
   * files. `RangeWarnings` had already met this exact problem on the dashboard banner and written
   * down the answer; `nameFor` is now shared rather than re-derived, so the two screens cannot come
   * to call the same block of numbers two different things.
   */
  const bookName = useMemo(() => {
    const byId = new Map((emissionPoints.data ?? []).map((p) => [p.id, p]));
    return (id: string) => {
      const point = byId.get(id);
      return point ? `${nameFor(point)} · ${point.businessName}` : null;
    };
  }, [emissionPoints.data]);

  const tips = reports.data?.tips;

  // Card tips and declared cash tips arrive as two lists keyed the same way. Side by side they
  // answer the question the owner actually has — what did this person earn tonight — which
  // neither list answers on its own.
  const tipLines = useMemo<TipLine[]>(() => {
    const byPerson = new Map<string, TipLine>();
    const add = (rows: TipBucket[] | undefined, field: 'nonCashMinor' | 'cashMinor') => {
      for (const row of rows ?? []) {
        const key = `${row.staffId}|${row.currency}`;
        const line =
          byPerson.get(key) ??
          ({
            key,
            staffId: row.staffId,
            currency: row.currency,
            nonCashMinor: 0,
            cashMinor: 0,
            totalMinor: 0,
          } as TipLine);
        line[field] += row.totalMinor;
        line.totalMinor += row.totalMinor;
        byPerson.set(key, line);
      }
    };
    add(tips?.nonCash, 'nonCashMinor');
    add(tips?.cashDeclared, 'cashMinor');
    return [...byPerson.values()].sort((a, b) => b.totalMinor - a.totalMinor);
  }, [tips]);

  const money = reports.data?.money;
  /**
   * Tips that arrived ON TOP of the bills, per currency — never inside "kept".
   *
   * A card tip is charged with the bill and is a debt to whoever earned it; a cash tip cannot be
   * rung at the register at all. Either way it is not the bar's money, and the old panel's "Tips
   * included" column said it was.
   */
  const tipsOnTop = useMemo(
    () => new Map((money?.payment.tipsByCurrency ?? []).map((row) => [row.currency, row.minor])),
    [money],
  );

  /**
   * How much of the change in each money went back to somebody who had not paid in it.
   *
   * That part is not taken off "kept" and cannot be: reducing the lempiras somebody paid by the
   * dollars they were given back means converting one into the other, and this page adds no two
   * currencies anywhere. So it is named on the change figure and again below it.
   */
  const crossedChange = useMemo(
    () => new Map((money?.payment.changeAcrossCurrencies ?? []).map((row) => [row.currency, row.minor])),
    [money],
  );

  const sessions = reports.data?.cash.sessions;

  // Only drawers that were actually counted are added up. A drawer closed without a count is not
  // "in balance" — it is unaccounted for, and folding it in as a zero would hide that.
  //
  // `off` is carried separately because the total nets out: one drawer L 500 over and another
  // L 500 short sum to nothing and would print "Exact", which is the one word that must not appear
  // over two drawers that were each wrong by five hundred lempiras.
  const cashTotals = useMemo(() => {
    const rows: { currency: string; minor: number; counted: number; off: number }[] = [];
    for (const [currency, pick] of [
      ['HNL', (s: DrawerSession) => s.overShortHnlMinor],
      ['USD', (s: DrawerSession) => s.overShortUsdMinor],
    ] as const) {
      const counted = (sessions ?? []).filter((s) => typeof pick(s) === 'number');
      if (counted.length === 0) continue;
      rows.push({
        currency,
        minor: counted.reduce((a, s) => a + (pick(s) ?? 0), 0),
        counted: counted.length,
        off: counted.filter((s) => pick(s) !== 0).length,
      });
    }
    return rows;
  }, [sessions]);

  const uncounted = (sessions ?? []).filter(
    (s) => s.status === 'closed' && s.overShortHnlMinor === null && s.overShortUsdMinor === null,
  ).length;

  const notNumbered = reports.data?.fiscal.notNumbered ?? [];
  const notNumberedTotal = notNumbered.reduce((n, r) => n + r.count, 0);
  // A deposit receipt has no number by design. Anything else on this list is a numbered document
  // that arrived without one, which no row in the table above can be checked for a gap.
  const unnumberedSurprises = notNumbered.filter((r) => r.type !== 'depositReceipt');
  const unnumberedSurprisesTotal = unnumberedSurprises.reduce((n, r) => n + r.count, 0);

  const applyPreset = (range: (day: string) => [string, string]) => {
    if (businessToday) setChosen(range(businessToday));
  };
  const isActive = (range: (day: string) => [string, string]) => {
    if (!businessToday) return false;
    const [presetFrom, presetTo] = range(businessToday);
    return presetFrom === from && presetTo === to;
  };

  const sales = reports.data?.sales;
  const loading = reports.isFetching;
  const failure = reports.isError ? errorCopy(reports.error, 'load these reports') : null;

  return (
    <>
      <Title order={3} mb="md">
        Reports
      </Title>

      <Paper withBorder p="md" radius="md" mb="md">
        <Group align="flex-end" gap="md" wrap="wrap">
          <DatePickerInput
            label="From"
            value={from || null}
            onChange={(value) => value && setChosen([value, to])}
            maxDate={to || undefined}
            valueFormat="D MMM YYYY"
            w={190}
          />
          <DatePickerInput
            label="To"
            value={to || null}
            onChange={(value) => value && setChosen([from, value])}
            minDate={from || undefined}
            valueFormat="D MMM YYYY"
            w={190}
          />
          <Group gap="xs">
            {PRESETS.map((preset) => (
              <Button
                key={preset.label}
                size="xs"
                variant={isActive(preset.range) ? 'light' : 'default'}
                onClick={() => applyPreset(preset.range)}
              >
                {preset.label}
              </Button>
            ))}
          </Group>
        </Group>
      </Paper>

      {failure ? (
        <Alert color="red" title={failure.title}>
          <Text size="sm" mb="sm">
            {failure.message}
          </Text>
          <Button size="xs" variant="light" onClick={() => void reports.refetch()}>
            Try again
          </Button>
        </Alert>
      ) : (
        <Stack gap={0}>
          <ReportSection
            title="What the bar sold"
            description="Everything invoiced in these dates, in the money each bill was issued in. Dollars and lempiras are never added together — one is not the other, and neither figure could be checked against a deposit if they were."
          >
            {/* One block per currency. These figures used to be a lempira total and a dollar total
                added into one number with no sign on it, under a note admitting it could not be
                banked — which is a figure that is true of neither currency and cannot be checked
                against a deposit, a platform statement or the SAR return. */}
            {money?.sales.length === 0 && !loading && (
              <Text size="sm" c="dimmed" mb="sm">
                Nothing was invoiced in these dates. If bills were paid, they are named below and the money
                answer further down is worked out from them rather than from the invoices.
              </Text>
            )}
            {(money?.sales ?? []).map((row) => (
              <div key={row.currency}>
                <Text size="sm" fw={600} mb={6}>
                  {enumLabel('currency', row.currency)}
                </Text>
                {/* The two headline figures are BOTH here and both labeled, because the menu is
                    written tax-inclusive: the number on the board is the number the guest pays, so
                    "total sales" is either what the guests handed over or what the bar keeps, and
                    they differ by the whole of the ISV. A single figure called "sales" would be one
                    of the two silently. */}
                <SimpleGrid cols={{ base: 2, md: 4 }} mb="md">
                  <Figure
                    label="What the guests paid"
                    value={formatMoney(row.totalInvoicedMinor, row.currency)}
                    hint="tax included"
                  />
                  <Figure
                    label="Sales before tax"
                    value={formatMoney(row.salesBeforeTaxMinor, row.currency)}
                    hint="the bar's share"
                  />
                  <Figure label="Tax charged" value={formatMoney(row.taxChargedMinor, row.currency)} />
                  <Figure label="Bills invoiced" value={row.documentCount} />
                </SimpleGrid>
              </div>
            ))}
            {/* MONEY REALLY TAKEN THAT NO FACTURA WAS EVER ISSUED FOR — and no guess at why.
                This used to end "check the block of numbers on the register that took it", which names
                one cause out of at least three and the rarest one at that. A factura is issued by a
                separate call against an already-settled bill, so during service most of this is
                paperwork that is seconds away; no register at this bar can issue one at all yet, so
                today this is every bill, every night; and a block of numbers really can run out.
                Nothing stored tells them apart, so the screen says what it knows and stops. */}
            {(money?.platformSales?.length ?? 0) > 0 && (
              <Stack gap="sm" mb="md">
                <Text fw={600}>Delivery platform sales</Text>
                <Text size="sm" c="dimmed">
                  Sales recorded when ordered. The platform pays these by weekly transfer; they are not cash
                  received at the register. Invoiced platform orders also appear in the fiscal figures above;
                  do not add both figures together.
                </Text>
                {(money?.platformSales ?? []).map((row) => (
                  <Group key={`${row.channelId}:${row.currency}`}>
                    <Text>
                      {row.source} · {row.orderCount} orders
                    </Text>
                    <Text fw={600}>{formatMoney(row.grossMinor, row.currency)}</Text>
                    <Text size="sm" c="dimmed">
                      Tax included: {formatMoney(row.taxMinor, row.currency)}
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}
            {(money?.settledWithoutInvoice.length ?? 0) > 0 && (
              <Alert color="orange" variant="light" mb="md">
                <Text size="sm">
                  {(money?.settledWithoutInvoice ?? [])
                    .map(
                      (row) =>
                        `${plural(row.checkCount, 'bill')} totaling ${formatMoney(row.totalMinor, row.currency)}`,
                    )
                    .join(', and ')}{' '}
                  {(money?.settledWithoutInvoice ?? []).reduce((n, r) => n + r.checkCount, 0) === 1
                    ? 'was'
                    : 'were'}{' '}
                  paid in these dates with no invoice against{' '}
                  {(money?.settledWithoutInvoice ?? []).reduce((n, r) => n + r.checkCount, 0) === 1
                    ? 'it'
                    : 'them'}{' '}
                  yet. That money is in none of the figures above, and it is in the margin below.
                </Text>
                <Text size="sm" mt="xs">
                  That can mean the invoice simply has not been raised yet, that no register here can raise
                  one, or that a block of numbers has run out — this page cannot tell which, so check before
                  you file.
                </Text>
              </Alert>
            )}
            {/* The other end of the note under "Tax to declare". Said here too because this is
                where the inflated figure is actually printed: a document with no tax split puts its
                whole value into "Sales before tax", so that figure and "Tax charged" beside it stop
                being two parts of the same sale. */}
            {(money?.salesWithoutTaxDetail.length ?? 0) > 0 && (
              <Alert color="yellow" variant="light" mb="md">
                <Text size="sm">
                  {withoutTaxSentence(money?.salesWithoutTaxDetail ?? [])} in these dates{' '}
                  {withoutTaxVerb(money?.salesWithoutTaxDetail ?? [], 'was issued', 'were issued')} without a
                  tax split. The whole of that value is counted under "Sales before tax" and none of it under
                  "Tax charged", and none of it reaches the "Tax to declare" table further down this page.
                </Text>
              </Alert>
            )}
            <SimpleGrid cols={{ base: 1, md: 2 }}>
              <DataTable
                idAccessor={(r) => `${r.type}|${r.currency}`}
                records={sales?.byType ?? []}
                fetching={loading}
                noRecordsText="Nothing was invoiced in these dates"
                columns={[
                  { accessor: 'type', title: 'Document', render: (r) => enumLabel('documentType', r.type) },
                  { accessor: 'count', title: 'How many', textAlign: 'right' },
                  {
                    accessor: 'totalMinor',
                    title: 'Total',
                    textAlign: 'right',
                    render: (r) => formatMoney(r.totalMinor, r.currency),
                  },
                ]}
              />
              <DataTable
                idAccessor={(r) => `${r.salesChannelId ?? 'walk-in'}|${r.currency}`}
                records={sales?.byChannel ?? []}
                fetching={loading}
                noRecordsText="Nothing was invoiced in these dates"
                columns={[
                  {
                    accessor: 'salesChannelId',
                    title: 'Ordered through',
                    // No channel on the order means an ordinary sale at the bar rather than one
                    // that arrived through a delivery platform.
                    render: (r) =>
                      r.salesChannelId ? <EntityLabel entity="channel" id={r.salesChannelId} /> : 'Walk-in',
                  },
                  { accessor: 'count', title: 'How many', textAlign: 'right' },
                  {
                    accessor: 'totalMinor',
                    title: 'Total',
                    textAlign: 'right',
                    render: (r) => formatMoney(r.totalMinor, r.currency),
                  },
                ]}
              />
            </SimpleGrid>
          </ReportSection>

          <ReportSection
            title="How people paid"
            description="What went onto the bills, by payment method, in the money it arrived in. Only payments that actually went through are counted — one that was backed out took nothing."
          >
            {(money?.payment.keptByCurrency.length ?? 0) > 0 && (
              <SimpleGrid cols={{ base: 2, md: 4 }} mb="sm">
                {(money?.payment.keptByCurrency ?? []).map((row) => (
                  <Figure
                    key={row.currency}
                    label={`${enumLabel('currency', row.currency)} kept`}
                    value={formatMoney(row.minor, row.currency)}
                    hint={
                      (tipsOnTop.get(row.currency) ?? 0) > 0
                        ? `${formatMoney(tipsOnTop.get(row.currency) ?? 0, row.currency)} of tips came in on top of this and is owed to staff`
                        : undefined
                    }
                  />
                ))}
              </SimpleGrid>
            )}
            <DataTable
              idAccessor={(r) => `${r.type}|${r.currency}`}
              records={money?.payment.tenders ?? []}
              fetching={loading}
              noRecordsText="No payments were taken in these dates"
              columns={[
                { accessor: 'type', title: 'How they paid', render: (r) => enumLabel('tenderType', r.type) },
                { accessor: 'currency', title: 'Money', render: (r) => enumLabel('currency', r.currency) },
                { accessor: 'count', title: 'Payments', textAlign: 'right' },
                // WHAT CROSSED THE COUNTER, not what went onto the bill. This column was headed
                // "Onto the bill" and printed the tender's face value, which includes the change:
                // a $20.00 note against a $15.00 tab put $15.00 on the bill and read as $20.00
                // here. What went onto a bill is an allocation, per payment and in the bill's
                // currency, and there is no per-tender version of it to print.
                {
                  accessor: 'handedOverMinor',
                  title: 'Handed over',
                  textAlign: 'right',
                  render: (r) => formatMoney(r.handedOverMinor, r.currency),
                },
                // ON TOP of the column beside it, and never inside it. This column used to be
                // headed "Tips included" next to one headed "Amount", which said the opposite of
                // the truth about both figures: a card charged $12.00 with $2.00 of tip on it puts
                // $10.00 on the bill, and the $2.00 is the server's.
                {
                  accessor: 'tipsMinor',
                  title: 'Tips on top',
                  textAlign: 'right',
                  render: (r) => formatMoney(r.tipsMinor, r.currency),
                },
              ]}
            />

            {/* Change and the round-up are their own figures rather than a correction applied to a
                tender row. Change is per PAYMENT — a guest paying in lempiras can be given dollars
                back — so taking it off a row would be inventing which note it came out of. */}
            {((money?.payment.changeGivenByCurrency.length ?? 0) > 0 ||
              (money?.payment.roundingByCurrency.length ?? 0) > 0) && (
              <SimpleGrid cols={{ base: 2, md: 4 }} mt="md">
                {(money?.payment.changeGivenByCurrency ?? []).map((row) => {
                  const crossed = crossedChange.get(row.currency) ?? 0;
                  return (
                    <Figure
                      key={`change-${row.currency}`}
                      label={`Change given back (${enumLabel('currency', row.currency).toLowerCase()})`}
                      value={formatMoney(row.minor, row.currency)}
                      hint={
                        crossed > 0
                          ? `${formatMoney(crossed, row.currency)} of it went back in a money nobody paid in, so that much is NOT taken off any figure above`
                          : 'already taken off what was kept'
                      }
                    />
                  );
                })}
                {/* THE ROUND-UP CAN GO EITHER WAY, so the direction is a word and never a minus.
                    It is positive when a bill rounded up onto what the guest owed and negative when
                    change across a currency boundary rounded up in the guest's favor — and the
                    label used to say "Rounded up in cash" over a figure printed "$ -0.40". */}
                {(money?.payment.roundingByCurrency ?? []).map((row) => (
                  <Figure
                    key={`rounding-${row.currency}`}
                    label={`Cash round-up (${enumLabel('currency', row.currency).toLowerCase()})`}
                    value={
                      <Directed
                        minor={row.minor}
                        currency={row.currency}
                        up="to the bar"
                        down="to the guest"
                      />
                    }
                    hint="there are no coins, so cash settles to whole units"
                  />
                ))}
              </SimpleGrid>
            )}

            {/* CHANGE HANDED BACK IN A MONEY NOBODY PAID IN, said out loud rather than netted.
                Subtracting it per currency printed "Lempiras kept L 2,000.00" beside "Dollars kept
                $ -50.00" — a pair that is only right if you add the two together, which is the one
                thing this page never does. */}
            {(money?.payment.changeAcrossCurrencies.length ?? 0) > 0 && (
              <Alert color="yellow" variant="light" mt="md">
                <Text size="sm">
                  {(money?.payment.changeAcrossCurrencies ?? [])
                    .map((row) => formatMoney(row.minor, row.currency))
                    .join(' and ')}{' '}
                  of change went back in a money nobody paid in — someone paid in one currency and was given
                  the other. It is not taken off anything above, because taking it off would mean turning
                  dollars into lempiras to do it. So "kept" is high by that much, in the money it left in.
                </Text>
              </Alert>
            )}

            {/* The two ways of settling a bill at this bar that write no payment row at all. Both
                were missing from every payment breakdown before this one, and one of them is
                exactly what he means when he says "transfers". */}
            {((money?.payment.onAccount.length ?? 0) > 0 ||
              (money?.payment.accountTransfers.length ?? 0) > 0) && (
              <>
                <Title order={6} mt="lg">
                  Money that did not come over the counter
                </Title>
                <Text size="sm" c="dimmed" mt={2} mb="sm" maw={880}>
                  A bill put on a regular's tab is a sale with no money behind it yet; the transfer that pays
                  it off is money in the bank with no sale behind it. Neither shows up as a payment above, so
                  both are listed here rather than left out.
                </Text>
                <SimpleGrid cols={{ base: 2, md: 4 }}>
                  {(money?.payment.onAccount ?? []).map((row) => (
                    <Figure
                      key={`account-${row.currency}`}
                      label={`Put on a tab (${enumLabel('currency', row.currency).toLowerCase()})`}
                      value={formatMoney(row.totalMinor, row.currency)}
                      hint={`${plural(row.count, 'bill')} — invoiced, not yet paid`}
                    />
                  ))}
                  {(money?.payment.accountTransfers ?? []).map((row) => (
                    <Figure
                      key={`transfer-${row.currency}`}
                      label={`Transfers into the bank (${enumLabel('currency', row.currency).toLowerCase()})`}
                      value={formatMoney(row.totalMinor, row.currency)}
                      hint={`${plural(row.count, 'transfer')} against an open tab`}
                    />
                  ))}
                </SimpleGrid>
              </>
            )}

            <Text size="xs" c="dimmed" mt="md" maw={880}>
              This table shows payments recorded for the selected dates, grouped by method and currency. A
              recorded card or digital payment should also be checked against its terminal, bank or
              payment-service confirmation. Putting a bill on a customer's tab does not collect money; those
              balances and account transfers are listed separately.
            </Text>
          </ReportSection>

          {/* Giveaways as their own figure rather than a hole in the sales. A comp and a discount
              are different acts — one is a plate that went out for nothing, the other is a sale for
              less — and pooling them hides which one happened. */}
          {(money?.givenAway.length ?? 0) > 0 && (
            <ReportSection
              title="What was given away"
              description="Money that came off the bills before the guest paid. It is not in the sales figures above, because it was never charged."
            >
              <SimpleGrid cols={{ base: 2, md: 4 }}>
                {/* A Fragment and not a wrapper element: a grid lays out its own children, so a
                    <div> around each pair would make one cell of two figures and the columns would
                    stop lining up with every other panel on the page. */}
                {(money?.givenAway ?? []).map((row) => (
                  <Fragment key={row.currency}>
                    <Figure
                      label={`Discounted (${enumLabel('currency', row.currency).toLowerCase()})`}
                      value={formatMoney(row.discountMinor, row.currency)}
                      hint="sold for less"
                    />
                    <Figure
                      label={`Comped (${enumLabel('currency', row.currency).toLowerCase()})`}
                      value={formatMoney(row.compMinor, row.currency)}
                      hint="went out for nothing"
                    />
                  </Fragment>
                ))}
              </SimpleGrid>
              <Text size="xs" c="dimmed" mt="sm" maw={880}>
                A bill comped in full comes to nothing and is issued no invoice, so it is in no sales figure
                on this page — but the food still left the shelf, and its cost is in the margin below. Your
                own tab is one of these.
              </Text>
            </ReportSection>
          )}

          {/* THE ANSWER TO THE QUESTION HE ASKED, and the sentence under it saying what it is not.
              Everything else on this page says what came in. This is the only thing on it that says
              what was left — and the only honest version of that is a gross margin, because whole
              categories of cost are not recorded anywhere in this system. */}
          <ReportSection
            title="Are we making money?"
            description="What came in after tax, less the food and drink that actually left the shelf. This is not profit — read what it leaves out, below."
          >
            {!money || money.goodsSold.billsCounted === 0 ? (
              <Nothing loading={loading}>
                No bills were closed in these dates, so there is nothing to work this out from.
              </Nothing>
            ) : (
              <>
                <SimpleGrid cols={{ base: 2, md: 4 }} mb="md">
                  {/* THE ONE FIGURE ON THIS PAGE THAT CROSSES A CURRENCY, and it says so.
                      A lempira bill and a dollar bill can only be added by converting one, and this
                      does it at each BILL's own recorded rate rather than at today's — the rate the
                      guest was actually charged at. Bills with no rate on them are not converted at
                      a guess; they are left out and counted, below. */}
                  <Figure
                    label="Came in, after tax"
                    value={formatMoney(money.goodsSold.revenueBaseMinor, money.baseCurrency)}
                    hint={`in ${enumLabel('currency', money.baseCurrency).toLowerCase()}, each bill converted at its own rate`}
                  />
                  <Figure
                    label="Food and drink that went out"
                    value={formatMoney(money.goodsSold.costBaseMinor, money.baseCurrency)}
                  />
                  {/* Unsigned, with the direction as a word. A month where the food cost more than
                      the food brought in prints "$223.55 short", never "$ -223.55" — the figure he
                      would quote is the one that must not be misread at a glance. */}
                  <Figure
                    label="Left over"
                    value={
                      <Directed
                        minor={money.goodsSold.grossMarginBaseMinor}
                        currency={money.baseCurrency}
                        up=""
                        down="short"
                      />
                    }
                    hint={
                      typeof money.goodsSold.marginBp === 'number'
                        ? money.goodsSold.marginBp < 0
                          ? `${Math.abs(money.goodsSold.marginBp / 100).toFixed(1)}% more went out than came in`
                          : `${(money.goodsSold.marginBp / 100).toFixed(1)}% of what came in`
                        : undefined
                    }
                  />
                  <Figure label="Bills counted" value={money.goodsSold.billsCounted} />
                </SimpleGrid>

                {/* Bills whose two moneys could not honestly be stated as one. They are in none of
                    the three figures above AND in none of the giveaway figures — so a comp on one
                    of them is invisible on this page unless this says so. */}
                {(money.goodsSold.billsWithNoRecordedRate > 0 ||
                  money.goodsSold.billsThatWouldNotAddUp > 0) && (
                  <Alert color="yellow" variant="light" mb="sm">
                    <Text size="sm">
                      {plural(
                        money.goodsSold.billsWithNoRecordedRate + money.goodsSold.billsThatWouldNotAddUp,
                        'bill',
                      )}{' '}
                      carried no usable rate between the two moneys, so nothing above could state{' '}
                      {money.goodsSold.billsWithNoRecordedRate + money.goodsSold.billsThatWouldNotAddUp === 1
                        ? 'it'
                        : 'them'}{' '}
                      in one currency.{' '}
                      {money.goodsSold.billsWithNoRecordedRate + money.goodsSold.billsThatWouldNotAddUp === 1
                        ? 'It is'
                        : 'They are'}{' '}
                      left out of these figures and out of what was given away, rather than converted at a
                      rate nobody charged.
                    </Text>
                  </Alert>
                )}

                {/* The dangerous direction is flattering, so the reasons a figure might be too good
                    are printed above the figure rather than in a footnote under it. */}
                {money.goodsSold.dishesWithNoCost > 0 && (
                  <Alert color="yellow" variant="light" mb="sm">
                    <Text size="sm">
                      {plural(money.goodsSold.dishesWithNoCost, 'dish')} sold in these dates and moved no
                      stock at all, so nothing above knows what{' '}
                      {money.goodsSold.dishesWithNoCost === 1 ? 'it' : 'they'} cost.{' '}
                      {money.goodsSold.dishesWithNoCost === 1 ? 'It is' : 'They are'} in none of these three
                      figures — see "What each dish makes" below for which.
                    </Text>
                  </Alert>
                )}
                {(money.goodsSold.unpricedIngredients > 0 || money.goodsSold.recipeFaults > 0) && (
                  <Alert color="yellow" variant="light" mb="sm">
                    <Text size="sm">
                      Some of what went out could not be priced, so the cost above is a floor and what is left
                      over is a ceiling. The real answer is smaller than this.
                    </Text>
                  </Alert>
                )}

                <Alert color="gray" variant="light" title="What this figure does not include">
                  <Text size="sm" mb="xs">
                    Nothing above carries any of these — they are not recorded anywhere in this system, so no
                    number here can take them off:
                  </Text>
                  <Text size="sm">
                    {money.costsNotRecorded
                      .map((code) => enumLabel('costNotRecorded', code).toLowerCase())
                      .join(', ')}
                    .
                  </Text>
                  <Text size="sm" mt="xs">
                    The costs this system does hold are listed below, unadded — they sit on different
                    calendars and adding them to the figure above would make a total about no period at all.
                  </Text>
                </Alert>
              </>
            )}
          </ReportSection>

          <ReportSection
            title="The costs this system does know about"
            description="Deliberately not added up, and deliberately not taken off the figure above. Each of these is measured over a different stretch of time."
          >
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              <div>
                <Text size="sm" fw={600} mb={6}>
                  Vendor bills
                </Text>
                {(money?.otherCosts.supplierBills.length ?? 0) === 0 ? (
                  <Nothing loading={loading}>No supplier bills are dated in these days.</Nothing>
                ) : (
                  <SimpleGrid cols={2}>
                    {(money?.otherCosts.supplierBills ?? []).map((row) => (
                      <Figure
                        key={row.currency}
                        label={enumLabel('currency', row.currency)}
                        value={formatMoney(row.totalMinor, row.currency)}
                        hint={plural(row.count, 'bill')}
                      />
                    ))}
                  </SimpleGrid>
                )}
                <Text size="xs" c="dimmed" mt="xs">
                  Dated by the day printed on the bill, not by the night you were trading — so a big delivery
                  on the last day of a month lands in that month whether or not any of it has been sold. The
                  ferry is inside these: what a crossing costs is spread over the goods it carried.
                </Text>
              </div>

              <div>
                <Text size="sm" fw={600} mb={6}>
                  Wages
                </Text>
                {(money?.otherCosts.payroll.length ?? 0) === 0 ? (
                  <Nothing loading={loading}>No pay period covers these dates.</Nothing>
                ) : (
                  (money?.otherCosts.payroll ?? []).map((run) => (
                    <div key={run.payrollRunId} style={{ marginBottom: 8 }}>
                      {/* WHETHER ANYBODY HAS SIGNED THIS OFF, beside the money and not only on the
                          wire. A register is a draft until it is approved, and a draft holds
                          whatever the last regeneration produced — printed as a bare total it looks
                          exactly like the wage bill on the page he judges the month by. */}
                      <Group gap="xs" mb={6}>
                        <Badge size="sm" color={enumColor('payrollRunStatus', run.status)} variant="light">
                          {enumLabel('payrollRunStatus', run.status)}
                        </Badge>
                        <Text size="xs" c="dimmed">
                          {run.periodStart} to {run.periodEnd}
                        </Text>
                      </Group>
                      <SimpleGrid cols={2}>
                        {run.totalsByCurrency.map((total) => (
                          <Figure
                            key={total.currency}
                            label={enumLabel('currency', total.currency)}
                            value={formatMoney(total.minor, total.currency)}
                          />
                        ))}
                      </SimpleGrid>
                      {run.status === 'draft' && (
                        <Text size="xs" c="orange.7" mt={4}>
                          Nobody has approved that register yet, so these are provisional figures — they will
                          change if it is worked out again.
                        </Text>
                      )}
                      {!run.withinRange && (
                        <Text size="xs" c="dimmed" mt={4}>
                          That pay period is longer than the dates above, so this is the whole period's wage
                          bill and not this stretch's share of it.
                        </Text>
                      )}
                    </div>
                  ))
                )}
              </div>

              <div>
                <Text size="sm" fw={600} mb={6}>
                  Commission to drivers and operators
                </Text>
                {(money?.otherCosts.referralCommission.length ?? 0) === 0 ? (
                  <Nothing loading={loading}>
                    Nothing was earned in referral commission in these dates.
                  </Nothing>
                ) : (
                  <SimpleGrid cols={2}>
                    {(money?.otherCosts.referralCommission ?? []).map((row) => (
                      <Figure
                        key={row.currency}
                        label={enumLabel('currency', row.currency)}
                        value={formatMoney(row.minor, row.currency)}
                      />
                    ))}
                  </SimpleGrid>
                )}
              </div>

              <div>
                <Text size="sm" fw={600} mb={6}>
                  Cash the drawers came up short
                </Text>
                {/* THE NET AND THE COUNT TOGETHER, always. One drawer L 500 over and another
                    L 500 short net to nothing, and a figure printed alone would say two registers that
                    were each wrong by five hundred lempiras were fine — which is the one thing this
                    number must never say. The count is what makes the net safe to read. */}
                {(money?.otherCosts.drawersCounted ?? 0) === 0 ? (
                  <Nothing loading={loading}>No drawer was counted in these dates.</Nothing>
                ) : (
                  <>
                    <SimpleGrid cols={2}>
                      {(money?.otherCosts.drawerOverShort ?? []).map((row) => (
                        <Figure
                          key={row.currency}
                          label={enumLabel('currency', row.currency)}
                          value={<OverShort minor={row.minor} currency={row.currency} />}
                        />
                      ))}
                    </SimpleGrid>
                    <Text
                      size="xs"
                      c={(money?.otherCosts.drawersOff ?? 0) > 0 ? 'orange.7' : 'dimmed'}
                      mt="xs"
                    >
                      {(money?.otherCosts.drawersOff ?? 0) === 0
                        ? `Every one of ${plural(money?.otherCosts.drawersCounted ?? 0, 'counted drawer')} balanced.`
                        : `Across ${plural(money?.otherCosts.drawersCounted ?? 0, 'counted drawer')}, ${money?.otherCosts.drawersOff} did not balance — see the drawer list further down.`}
                    </Text>
                  </>
                )}
                {(money?.otherCosts.drawersNotCounted ?? 0) > 0 && (
                  <Text size="xs" c="orange.7" mt="xs">
                    {plural(money?.otherCosts.drawersNotCounted ?? 0, 'drawer')} closed without being counted,
                    so nothing above accounts for the cash in{' '}
                    {(money?.otherCosts.drawersNotCounted ?? 0) === 1 ? 'it' : 'them'}.
                  </Text>
                )}
              </div>
            </SimpleGrid>
          </ReportSection>

          {/* Directly under the money answer, because it is that answer broken down the one way the
              panels above cannot break it down: per dish, against what the dish cost. It renders its
              own section because it fetches its own range and has to be able to say that nothing was
              billed. */}
          <MenuProfitability from={from} to={to} />

          {/* Keep the operational failure beside the dish economics: a remake is not another sale,
              and this is where its failed food, replacement and guest credit remain visible. */}
          <QualityRecovery from={from} to={to} />

          {/* The report every giveaway guard in the register is aimed at: who each comp, break and
              staff meal was FOR, how the beneficiary was confirmed, and the running totals the
              daily allowances refuse past. It renders its own section because it fetches its own
              range and must be able to say nothing was given away. */}
          <Exceptions from={from} to={to} />

          {/* The other half of the same question the drawer report below asks. That one compares
              what the register says it took against what is in the drawer; this compares what the
              recipes say was poured against what is on the shelf. It renders its own section
              because it fetches its own range and must be able to say nothing was counted. */}
          <StockVariance from={from} to={to} />

          {/* What the register says it took is not what is in the drawer. The difference is the only
              figure on this page that finds a mis-ring or a hand in the register, and it was the one
              report with an endpoint and no screen — so nobody could see it at all. */}
          <ReportSection
            title="Cash drawers"
            description="Every drawer opened in these dates, and how the count at the end compared with what the register expected."
          >
            {cashTotals.length > 0 && (
              <SimpleGrid cols={{ base: 2, md: 4 }} mb="sm">
                {cashTotals.map((row) => (
                  <Figure
                    key={row.currency}
                    label={`${enumLabel('currency', row.currency)} — over or short`}
                    value={<OverShort minor={row.minor} currency={row.currency} />}
                    hint={
                      row.off === 0
                        ? `every one of ${row.counted} counted drawer${row.counted === 1 ? '' : 's'} balanced`
                        : `across ${row.counted} counted drawer${row.counted === 1 ? '' : 's'}, ${row.off} did not balance`
                    }
                  />
                ))}
              </SimpleGrid>
            )}
            {uncounted > 0 && (
              <Alert color="yellow" variant="light" mb="sm">
                <Text size="sm">
                  {uncounted === 1 ? 'One drawer was' : `${uncounted} drawers were`} closed without being
                  counted, so nothing above accounts for the cash in {uncounted === 1 ? 'it' : 'them'}.
                </Text>
              </Alert>
            )}
            <DataTable
              idAccessor="drawerSessionId"
              records={reports.data?.cash.sessions ?? []}
              fetching={loading}
              noRecordsText="No drawers were opened in these dates"
              columns={[
                {
                  accessor: 'ownerStaffId',
                  title: 'Whose drawer',
                  render: (r) => <EntityLabel entity="staff" id={r.ownerStaffId} />,
                },
                { accessor: 'openedAt', title: 'Opened', render: (r) => formatDateTime(r.openedAt) },
                {
                  accessor: 'closedAt',
                  title: 'Closed',
                  render: (r) =>
                    r.status === 'open' ? (
                      <Text size="sm" c="orange">
                        Still open
                      </Text>
                    ) : (
                      formatDateTime(r.closedAt)
                    ),
                },
                {
                  accessor: 'overShortHnlMinor',
                  title: 'Lempiras',
                  textAlign: 'right',
                  render: (r) => <OverShort minor={r.overShortHnlMinor} currency="HNL" />,
                },
                {
                  accessor: 'overShortUsdMinor',
                  title: 'Dollars',
                  textAlign: 'right',
                  render: (r) => <OverShort minor={r.overShortUsdMinor} currency="USD" />,
                },
              ]}
            />
          </ReportSection>

          {/* Beside the tips rather than on the partner screens, because it answers the same kind
              of question as everything else on this page — what a stretch of trading actually did —
              and the partner screens answer the other kind: what one person is owed right now. It
              fetches its own range for the same reason the pour report does. */}
          <ReferralLeaderboard from={from} to={to} />

          <ReportSection
            title="Tips"
            description="What each person earned in tips in these dates. Card and transfer tips are held here until you pay them out; cash tips are what staff declared at the end of their shift."
          >
            <DataTable
              idAccessor="key"
              records={tipLines}
              fetching={loading}
              noRecordsText="No tips recorded in these dates"
              columns={[
                {
                  accessor: 'staffId',
                  title: 'Person',
                  render: (r) => <EntityLabel entity="staff" id={r.staffId} />,
                },
                { accessor: 'currency', title: 'Money', render: (r) => enumLabel('currency', r.currency) },
                {
                  accessor: 'nonCashMinor',
                  title: 'Card and transfer',
                  textAlign: 'right',
                  render: (r) => formatMoney(r.nonCashMinor, r.currency),
                },
                {
                  accessor: 'cashMinor',
                  title: 'Cash declared',
                  textAlign: 'right',
                  render: (r) => formatMoney(r.cashMinor, r.currency),
                },
                {
                  accessor: 'totalMinor',
                  title: 'Total',
                  textAlign: 'right',
                  render: (r) => (
                    <Text fw={600} size="sm">
                      {formatMoney(r.totalMinor, r.currency)}
                    </Text>
                  ),
                },
              ]}
            />

            <Title order={6} mt="lg">
              Tips still owed
            </Title>
            {/* This one ignores the dates on purpose — it is the running balance of everything
                never paid out, which is exactly the number that must not look like a range. */}
            <Text size="sm" c="dimmed" mt={2} mb="sm">
              Everything not yet handed over, from the very beginning. The dates above do not change these
              amounts.
            </Text>
            <DataTable
              idAccessor={(r) => `${r.staffId}|${r.currency}`}
              records={tips?.unpaidLiability ?? []}
              fetching={loading}
              noRecordsText="Everyone has been paid their tips"
              columns={[
                {
                  accessor: 'staffId',
                  title: 'Person',
                  render: (r) => <EntityLabel entity="staff" id={r.staffId} />,
                },
                { accessor: 'currency', title: 'Money', render: (r) => enumLabel('currency', r.currency) },
                {
                  accessor: 'totalMinor',
                  title: 'Owed',
                  textAlign: 'right',
                  render: (r) => formatMoney(r.totalMinor, r.currency),
                },
              ]}
            />
          </ReportSection>

          {/*
            One row per TAX, and the base it was charged on beside it — which is how a SAR line is
            filled in, and what this table used to make impossible.

            A West End sale carries ISV 18% and the ZOLITUR 4% on the same money, and the report's
            ledger keeps the base with the PAIR so that the bar's turnover is not declared twice.
            Poured straight into one table that gave the owner three rows to copy onto a government
            form and each of them was false alone: "ISV 18% — sales before tax $0.00, tax charged
            $2.06, total $0.00", beside a combined row declaring $11.48 of sales that charged no tax,
            on which $11.48 + $0.00 came to $14.00.

            So the base is repeated under every tax levied on it, the sentence under the table says
            the sales column must not be added down, and there is no "Total" column at all, because
            a total per tax on a shared base is not a figure that exists.
          */}
          <ReportSection
            title="Tax to declare"
            description="What each tax was charged on and what it came to, ready for the monthly return. The return is filed in lempiras, so dollar sales are listed on their own rows."
          >
            {/*
              WHAT THIS TABLE COULD NOT COUNT, above the table rather than under it.

              A credit note issued before the tax on one was worked out carries no split at all, so
              it leaves the sales total and nothing else: the page then read "Sales before tax $8.48"
              at the top and "charged on $11.48" here, and the higher figure is the one that gets
              copied onto the return. Both were right about the documents they had read. Nothing said
              they had read different documents, which is the only thing that turns two figures into
              a contradiction. No breakdown is invented for those documents — a tax figure made up
              afterwards for a document issued without one would be worse than the gap — so the size
              of the gap is stated instead.
            */}
            {(reports.data?.tax.withoutTaxDetail.length ?? 0) > 0 && (
              <Alert color="yellow" variant="light" mb="sm">
                <Text size="sm">
                  {withoutTaxSentence(reports.data?.tax.withoutTaxDetail ?? [])} in these dates{' '}
                  {withoutTaxVerb(reports.data?.tax.withoutTaxDetail ?? [], 'carries', 'carry')} no tax split,
                  so none of that value is in this table. The sales figures at the top of this page do include
                  it, which is why the two do not agree — they are counting different documents, not the same
                  money twice.
                </Text>
              </Alert>
            )}
            <DataTable
              idAccessor={(r) => `${r.currency}|${r.code}`}
              records={reports.data?.tax.byTax ?? []}
              // Waits for the tax sets too. Drawing the rows first would spell the stored codes —
              // ISV15, ISV15+TUR4 — onto the one table that gets copied onto a government form,
              // and a figure read off a row labelled in our vocabulary is a figure read twice.
              fetching={loading || taxGroups.isLoading}
              noRecordsText="No taxable sales in these dates"
              columns={[
                { accessor: 'code', title: 'Tax', render: (r) => taxName(r.code) },
                { accessor: 'currency', title: 'Money', render: (r) => enumLabel('currency', r.currency) },
                {
                  accessor: 'netMinor',
                  title: 'Charged on',
                  textAlign: 'right',
                  render: (r) => formatMoney(r.netMinor, r.currency),
                },
                {
                  accessor: 'taxMinor',
                  title: 'Tax charged',
                  textAlign: 'right',
                  render: (r) => formatMoney(r.taxMinor, r.currency),
                },
              ]}
            />
            {(reports.data?.tax.byTax.length ?? 0) > 0 && (
              <Text size="xs" c="dimmed" mt="sm" maw={860}>
                A sale that carries two taxes is listed under both, so{' '}
                <b>the "charged on" column is not a column to add up</b> — the same drinks appear under ISV
                and again under the tourism tax because both were charged on them. What was actually sold and
                taken is in the sales and money sections at the top of this page.
              </Text>
            )}
          </ReportSection>

          {/* Every word here says "book of numbers" rather than "invoice", because a credit note
              range is one of these rows and its numbers are not invoice numbers — the same
              distinction the invoicing screen and the dashboard banner already draw. */}
          <ReportSection
            title="Numbering"
            description="Every book of numbers has to run without gaps. A missing number needs an explanation, so check this before you file."
          >
            <DataTable
              idAccessor="emissionPointId"
              records={reports.data?.fiscal.byEmissionPoint ?? []}
              fetching={loading}
              noRecordsText="Nothing numbered was issued in these dates"
              columns={[
                {
                  accessor: 'emissionPointId',
                  title: 'Book of numbers',
                  render: (r) =>
                    bookName(r.emissionPointId) ?? (
                      <Text size="sm" c="dimmed">
                        Not on file
                      </Text>
                    ),
                },
                { accessor: 'count', title: 'Numbers used', textAlign: 'right' },
                {
                  accessor: 'firstNumber',
                  title: 'First',
                  textAlign: 'right',
                  render: (r) => invoiceNumber(r.firstNumber),
                },
                {
                  accessor: 'lastNumber',
                  title: 'Last',
                  textAlign: 'right',
                  render: (r) => invoiceNumber(r.lastNumber),
                },
                {
                  accessor: 'gaps',
                  title: 'Missing numbers',
                  render: (r) =>
                    r.gaps.length === 0 ? (
                      <Badge color="teal" variant="light">
                        None
                      </Badge>
                    ) : (
                      <Group gap="xs" wrap="nowrap">
                        <Badge color="red" variant="light">
                          {r.gaps.length} missing
                        </Badge>
                        <Text size="xs" c="dimmed">
                          {r.gaps.slice(0, 8).map(invoiceNumber).join(', ')}
                          {r.gaps.length > 8 ? '…' : ''}
                        </Text>
                      </Group>
                    ),
                },
              ]}
            />
            {/*
              The count and the table are two different sets, and the difference used to be left for
              the reader to work out and then explained with a guess. "Deposit receipts carry no
              invoice number, so they are not listed above" was printed whatever the missing rows
              actually were — including an invoice that reached the register with no number on it,
              which is precisely what this screen exists to catch.
            */}
            {reports.data && (
              <Text size="xs" c="dimmed" mt="xs">
                {plural(reports.data.fiscal.documents, 'document')} issued in these dates.{' '}
                {reports.data.fiscal.notNumbered.length === 0
                  ? 'Every one of them carries a number and is counted above.'
                  : `${plural(
                      notNumberedTotal,
                      'document',
                    )} of those carry no number and are not in the table above: ${reports.data.fiscal.notNumbered
                      .map((r) => plural(r.count, enumLabel('documentType', r.type).toLowerCase()))
                      .join(', ')}.`}
              </Text>
            )}
            {unnumberedSurprises.length > 0 && (
              <Alert color="orange" variant="light" mt="xs">
                <Text size="sm">
                  A deposit receipt is not a numbered document, but everything else here should be, and{' '}
                  {unnumberedSurprises
                    .map((r) => plural(r.count, enumLabel('documentType', r.type).toLowerCase()))
                    .join(', ')}{' '}
                  in these dates {unnumberedSurprisesTotal === 1 ? 'has' : 'have'} none. Nothing above can
                  check {unnumberedSurprisesTotal === 1 ? 'it' : 'them'} for a missing number — look{' '}
                  {unnumberedSurprisesTotal === 1 ? 'it' : 'them'} up before you file.
                </Text>
              </Alert>
            )}
          </ReportSection>
        </Stack>
      )}
    </>
  );
}
